import React, { useState, useEffect } from "react";
import { Card } from "@shared/ui/card";
import { ChevronDown, ChevronUp, Eye } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";


export const FieldConfigurationTab = ({ meterReadingFormatDetail }: { meterReadingFormatDetail: any }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (meterReadingFormatDetail?.sections?.length > 0) {
      // Expand the first section by default
      setExpandedSections({
        [meterReadingFormatDetail.sections[0].section]: true
      });
    }
  }, [meterReadingFormatDetail]);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const dataCollectionMethod = meterReadingFormatDetail?.dataCollectionMethod?.name;
  const methodStr = String(dataCollectionMethod || "").toLowerCase();
  const isAutomated = methodStr === "automated" || methodStr.includes("automated");
  const isMobile = methodStr === "mobile" || methodStr.includes("mobile") || methodStr.includes("app");

  // 1. Resolve Vendor Details if applicable
  const vendorName = meterReadingFormatDetail?.vendor;
  // const vendorInfo = vendorName ? VENDOR_DETAILS[vendorName] : null;
   const vendorInfo =  meterReadingFormatDetail?.vendor;

  // 2. Resolve Sections (Mobile Mode)
  const sections = meterReadingFormatDetail?.sections || [];
  const enabledSections = sections.filter((s: any) => 
    s.enabled !== false && 
    s.isActive !== false && 
    s.section !== "Format Fields"
  );

  // 3. Resolve all format fields
  let rawFields: any[] = [];
  
  if (meterReadingFormatDetail?.formatFields) {
    rawFields = meterReadingFormatDetail.formatFields;
  } else if (sections && sections.length > 0) {
    const formatFieldsSection = sections.find((s: any) => s.section === "Format Fields");
    rawFields = formatFieldsSection?.fields || [];
  } else if (meterReadingFormatDetail?.configurationJson) {
    rawFields = Object.values(meterReadingFormatDetail.configurationJson);
  }

  const getOptionValues = (field: any): string[] => {
    if (Array.isArray(field.options)) {
      return field.options.map((opt: any) => typeof opt === "string" ? opt : (opt.label || opt.value || ""));
    }
    if (Array.isArray(field.values)) {
      return field.values.map((val: any) => typeof val === "string" ? val : (val.value || val.label || ""));
    }
    return [];
  };

  // Group fields by category
  const vendorFields: any[] = [];
  const systemFields: any[] = [];
  const customFields: any[] = [];

  rawFields.forEach((f: any) => {
    // Only process if it belongs to "Format Fields" or lacks a section (if it's a flat array)
    if (f.section && f.section !== "Format Fields") {
      return;
    }
    const source = (f.source || f.inputType || f.input_type || "").toLowerCase();
    if (source === "vendor") {
      vendorFields.push(f);
    } else if (source === "fixed") {
      systemFields.push(f);
    } else if (source === "custom") {
      customFields.push(f);
    }
  });

  const columns: TableColumn<any>[] = [
    {
      key: "index",
      header: "#",
      width: "60px",
      render: (_value: any, _row: any, index: number) => (
        <span className="font-medium text-gray-400">{index + 1}</span>
      ),
    },
    {
      key: "fieldName",
      header: "Field Label",
      render: (_value: any, row: any) => {
        const label = row.label || row.fieldName || row.field_name || "—";
        const options = getOptionValues(row);
        return (
          <div className="py-1">
            <div className="font-semibold text-gray-950">{label}</div>
            {options.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {options.map((opt) => (
                  <span key={opt} className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100 text-[10px] font-bold">
                    {opt}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "type",
      header: "Type",
      width: "160px",
      render: (_value: any, row: any) => {
        const type = row.type || row.fieldType || row.field_type || "Text";
        let typeBadgeColor = "bg-gray-50 text-gray-700 border-gray-200";
        let typePrefix = "";
        if (type === "Number") {
          typeBadgeColor = "bg-blue-50 text-blue-700 border-blue-100";
          typePrefix = "# ";
        } else if (type === "Date") {
          typeBadgeColor = "bg-green-50 text-green-700 border-green-100";
          typePrefix = "D ";
        } else if (type === "Time") {
          typeBadgeColor = "bg-amber-50 text-amber-700 border-amber-100";
        } else if (type === "Dropdown") {
          typeBadgeColor = "bg-purple-50 text-purple-700 border-purple-100";
          typePrefix = "• ";
        }
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${typeBadgeColor}`}>
            {typePrefix}{type}
          </span>
        );
      },
    },
    {
      key: "required",
      header: "Required",
      width: "130px",
      render: (_value: any, row: any) => {
        const required = row.required ?? false;
        return (
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${
            required 
              ? "bg-red-50 text-red-650 border border-red-100" 
              : "bg-gray-150 text-gray-650 border border-gray-200"
          }`}>
            {required ? "Required" : "Optional"}
          </span>
        );
      },
    },
  ];

  const renderTable = (title: string, fieldsList: any[], titleColor: string) => {
    if (fieldsList.length === 0) return null;
    return (
      <div className="space-y-3">
        <h4 className={`text-xs font-bold tracking-wider ${titleColor}`}>{title}</h4>
        <div className="border border-gray-100 rounded-lg overflow-hidden bg-white shadow-sm [&_th]:text-xs [&_th]:font-semibold [&_th]:text-gray-500 [&_th]:py-3 [&_th]:px-4">
          <GenericTable
            data={fieldsList}
            columns={columns}
            rowKey={(row: any) => row.fieldName || row.field_name || row.label || ""}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 2. Vendor Details Card */}
      {isAutomated && (
        <Card className="p-6 border border-blue-100 bg-blue-50/20 shadow-sm space-y-4">
          <h3 className="text-md font-bold text-blue-900">Vendor Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-sm">
            <div>
              <span className="text-xs text-gray-400 block font-semibold mb-0.5">Vendor Name</span>
              <span className="text-gray-800 font-medium">{vendorInfo?.name ?? "NA"}</span>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold mb-0.5">Protocol</span>
              <span className="text-gray-800 font-medium">{vendorInfo?.protocol ?? "NA"}</span>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold mb-0.5">Website</span>
              <a href={`https://${vendorInfo?.companyWebsite}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline font-medium">
                {vendorInfo?.companyWebsite ?? "NA"}
              </a>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold mb-0.5">Support Email</span>
              <span className="text-gray-800 font-medium">{vendorInfo?.email ?? "NA"}</span>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold mb-0.5">Support Phone</span>
              <span className="text-gray-800 font-medium">{vendorInfo?.phone ?? "NA"}</span>
            </div>
            <div className="col-span-2 md:col-span-3">
              <span className="text-xs text-gray-400 block font-semibold mb-0.5">Description</span>
              <p className="text-gray-600 leading-relaxed">{vendorInfo?.description ?? "NA"}</p>
            </div>
          </div>
        </Card>
      )}

      {/* 3. Details Sections Card (Mobile only) */}
      {isMobile && enabledSections.length > 0 && (
        <Card className="p-6 border border-gray-200 shadow-sm space-y-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Details Sections</h3>
            <p className="text-sm text-gray-500 mt-0.5">Predefined sections configured for the mobile reading flow.</p>
          </div>

          <div className="space-y-4">
            {enabledSections.map((section: any, idx: number) => {
              const sectionId = section.section;
              const isOpen = expandedSections[sectionId] ?? false;
              const fields = section.fields || [];
              const enabledFields = fields.filter((f: any) => f.enabled !== false && f.isActive !== false);

              return (
                <div key={idx} className="border border-gray-150 rounded-xl overflow-hidden shadow-sm">
                  {/* Accordion Header */}
                  <div
                    onClick={() => toggleSection(sectionId)}
                    className={`flex items-center justify-between p-4 cursor-pointer transition-all border-b ${
                      isOpen ? "bg-gray-50/50 border-gray-150" : "bg-white border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <h4 className="font-bold text-gray-800 text-sm">{sectionId}</h4>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200/60 text-gray-700">
                        {enabledFields.length} / {fields.length} selected
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                        Auto-populated
                      </span>
                    </div>
                    <button type="button" className="p-1 hover:bg-gray-100 rounded text-gray-600">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Accordion Body */}
                  {isOpen && (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-white">
                      {enabledFields.map((field: any, fIdx: number) => {
                        const fieldLabel = field.label || field.fieldName || field.field_name;
                        const fieldType = field.type || field.fieldType || field.field_type;
                        return (
                          <div key={fIdx} className="p-4 rounded-lg border border-gray-150 bg-white flex items-center gap-3 shadow-sm hover:border-gray-250 transition-all">
                            <Eye className="w-4 h-4 text-green-650 flex-shrink-0" />
                            <div>
                              <span className="text-sm font-semibold text-gray-800 block leading-tight">{fieldLabel}</span>
                              <span className="text-xs text-blue-600 font-medium block mt-0.5">{fieldType}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* 4. Format Fields Card */}
      <Card className="p-6 border border-gray-200 shadow-sm space-y-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Format Fields</h3>
          <p className="text-sm text-gray-500 mt-0.5">Predefined and custom format fields for data validation.</p>
        </div>

        <div className="space-y-6">
          {renderTable("Vendor Fields", vendorFields, "text-blue-600")}
          {renderTable("System Fields", systemFields, "text-purple-600")}
          {renderTable("Custom Fields", customFields, "text-green-600")}
        </div>
      </Card>
    </div>
  );
};

